import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "@/lib/api";
import { FileText, CreditCard, ArrowRight, Loader2, ShieldCheck, Lock, Landmark, Copy } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";

export default function PayHub() {
  const [tab, setTab] = useState("invoice");
  const [bank, setBank] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/company`).then((r) => setBank(r.data)).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      <div data-testid="pay-hub-page">
        <section className="relative">
          <div className="bg-stage" />
          <div className="absolute inset-0 pointer-events-none">
            <div className="bg-grid" />
            <div className="orb orb-green" />
          </div>
          <div className="relative z-10 max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 pt-16 pb-8 sm:pt-24 text-center">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[color:var(--ivory-muted)] mb-3">Make a payment</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Secure payments via <span className="text-[color:var(--bd-green-bright)]">Stripe</span>.
            </h1>
            <p className="mt-4 max-w-xl mx-auto text-[color:var(--ivory-muted)]">
              Pay an existing invoice or make a quick custom payment for a booking, deposit, or service.
            </p>
          </div>
        </section>

        <section className="pb-12">
          <div className="max-w-[640px] mx-auto px-5 sm:px-8 lg:px-12">
            <div className="flex gap-1 p-1 rounded-full border border-white/10 bg-white/[0.02] w-fit mx-auto mb-6" data-testid="pay-tabs">
              <PayTab active={tab === "invoice"} onClick={() => setTab("invoice")} testId="tab-invoice"><FileText className="w-4 h-4" /> Pay invoice</PayTab>
              <PayTab active={tab === "quick"} onClick={() => setTab("quick")} testId="tab-quick"><CreditCard className="w-4 h-4" /> Quick payment</PayTab>
            </div>

            {tab === "invoice" ? <PayInvoiceForm /> : <QuickPaymentForm />}

            <div className="mt-8 flex items-center justify-center gap-6 text-xs text-[color:var(--ivory-muted)]">
              <div className="flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> SSL secured</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[color:var(--bd-green-bright)]" /> Powered by Stripe</div>
            </div>
          </div>
        </section>

        {/* Bank transfer alternative */}
        {bank && bank.iban && (
          <section className="pb-20" data-testid="bank-details-section">
            <div className="max-w-[760px] mx-auto px-5 sm:px-8 lg:px-12">
              <div className="relative rounded-3xl border border-[color:var(--bd-green)]/30 overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 flex">
                  <div className="flex-[2.2] bg-gradient-to-r from-[#006A4E] via-[#00A36C] to-[#006A4E]" />
                  <div className="flex-1 bg-gradient-to-r from-[#F42A41] to-[#DA291C]" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--bd-green)]/8 via-transparent to-[color:var(--bd-red)]/5 pointer-events-none" />
                <div className="relative p-6 sm:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 shrink-0 rounded-full bg-[color:var(--bd-green)]/15 flex items-center justify-center">
                      <Landmark className="w-5 h-5 text-[color:var(--bd-green-bright)]" />
                    </div>
                    <div className="flex-1">
                      <div className="text-[11px] tracking-[0.3em] uppercase text-[color:var(--bd-green-bright)] mb-1">Alternative: Bank transfer</div>
                      <h3 className="font-display text-2xl sm:text-3xl">Pay directly via SEPA / SWIFT</h3>
                      <p className="text-sm text-[color:var(--ivory-muted)] mt-2 leading-relaxed">
                        Prefer transfer? Send the payment to our Danske Bank account below. Please reference your invoice number or your name in the transfer message.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    <BankRow label="Account holder" value={bank.company_name} />
                    <BankRow label="Bank" value={bank.bank_name} />
                    <BankRow label="Account number" value="12350701808" mono />
                    <BankRow label="BIC / SWIFT" value={bank.bic} mono />
                    <div className="sm:col-span-2">
                      <BankRow label="IBAN" value={bank.iban} mono prominent />
                    </div>
                  </div>

                  <div className="mt-5 pt-5 border-t border-white/10 text-xs text-[color:var(--ivory-muted)]">
                    For SEPA transfers from EU banks, IBAN + BIC is enough. International transfers may take 1–3 business days. Email <a href="mailto:info@bdtt.se" className="text-white hover:text-[color:var(--bd-green-bright)]">info@bdtt.se</a> once paid so we can mark your invoice as settled.
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </PublicLayout>
  );
}

function BankRow({ label, value, mono, prominent }) {
  const copy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };
  return (
    <div className={`rounded-xl border border-white/10 bg-[#0E1216]/70 p-3 ${prominent ? "border-[color:var(--bd-green)]/30 bg-[color:var(--bd-green)]/5" : ""}`}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[color:var(--ivory-muted)] mb-1">{label}</div>
      <div className="flex items-center justify-between gap-2">
        <div className={`${mono ? "font-mono" : ""} ${prominent ? "text-lg" : "text-sm"} text-white break-all`}>
          {value || "—"}
        </div>
        {value && (
          <button type="button" onClick={copy} className="p-1.5 rounded-md text-[color:var(--ivory-muted)] hover:text-white hover:bg-white/[0.06] transition" aria-label={`Copy ${label}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function PayTab({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-2.5 rounded-full text-sm transition inline-flex items-center gap-2 ${
        active
          ? "bg-[color:var(--bd-green)]/25 text-white border border-[color:var(--bd-green)]/40"
          : "text-[color:var(--ivory-muted)] hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function PayInvoiceForm() {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    const v = invoiceNumber.trim();
    if (!v) { toast.error("Enter your invoice number"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/find-invoice`, { invoice_number: v });
      toast.success(`Invoice ${data.invoice_number} found`);
      navigate(`/pay/${data.public_token}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't find that invoice.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-6 sm:p-8 rounded-2xl border border-white/10 bg-[#0E1216]/80 backdrop-blur" data-testid="pay-invoice-form">
      <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--ivory-muted)] mb-2">Invoice number</div>
      <div className="relative">
        <FileText className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--ivory-muted)] pointer-events-none" />
        <input
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
          placeholder="e.g. INV-01024"
          required
          className="email-input pl-10 uppercase tracking-wide"
          data-testid="pay-invoice-number"
        />
      </div>
      <p className="mt-3 text-xs text-[color:var(--ivory-muted)]">
        You'll find your invoice number on the email or PDF we sent you.
      </p>
      <button
        type="submit"
        disabled={submitting}
        className="mt-6 w-full pill pill-primary inline-flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="pay-invoice-submit"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {submitting ? "Looking up…" : "Find & pay invoice"}
      </button>
    </form>
  );
}

function QuickPaymentForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/payments/quick-checkout`, {
        amount: amt,
        currency: "SEK",
        name: name.trim(),
        email: email.trim(),
        description: description.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not start payment");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-6 sm:p-8 rounded-2xl border border-white/10 bg-[#0E1216]/80 backdrop-blur space-y-4" data-testid="quick-pay-form">
      <Field label="Your name" value={name} onChange={setName} placeholder="John Doe" required testId="quick-name" />
      <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" required testId="quick-email" />
      <div className="grid sm:grid-cols-[1fr_120px] gap-3">
        <Field label="Amount" type="number" step="0.01" min="1" value={amount} onChange={setAmount} placeholder="5000.00" required testId="quick-amount" />
        <div>
          <label className="text-xs uppercase tracking-[0.25em] text-[color:var(--ivory-muted)] mb-2 block">Currency</label>
          <div className="email-input pl-4 flex items-center text-[color:var(--ivory-muted)] cursor-not-allowed">SEK (kr)</div>
        </div>
      </div>
      <Field label="What is this payment for?" value={description} onChange={setDescription} placeholder="e.g. Deposit for Dhaka trip, Sept 2026" testId="quick-description" />
      <button
        type="submit"
        disabled={submitting}
        className="w-full pill pill-primary inline-flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="quick-pay-submit"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
        {submitting ? "Redirecting…" : `Pay${amount && Number(amount) > 0 ? ` ${Number(amount).toLocaleString()} kr` : ""} securely`}
      </button>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, step, min, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.25em] text-[color:var(--ivory-muted)] mb-2 block">
        {label}{required && <span className="text-[color:var(--bd-red-bright)]"> *</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        step={step}
        min={min}
        className="email-input pl-4"
        data-testid={testId}
      />
    </div>
  );
}
